import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import {
  getUserAttributes,
  shouldTriggerProjectSelectionDialog,
  CustomEventName,
  StructuredProject,
} from '@mb/lib/';
import { postFetcher, getSearchConfig } from '@mb/lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import useSWRMutation from 'swr/mutation';
import { sendAnalytics } from '../../../api/events';
import { ProjectAnalyticsProps } from '../../../components/Cards/ResultCard/types';
import { getEventsMaterialProps } from '../../../helpers';
import { extraFieldsList } from '../../../hooks';

import useAddToBag from '../../../hooks/useAddToBag';
import { activeContentBlockDataSelector } from '../../../redux/selectors/commonSelector';
import { xRequestIdSelector } from '../../../redux/selectors/searchSelector';
import {
  setContentBlocksModalOpen,
  setActiveContentBlock,
} from '../../../redux/sliceCreators/commonSlice';
import { setContentBlockXRequestId } from '../../../redux/sliceCreators/searchSlice';
import { ProductSearchMaterial } from '../../../types';
import {
  checkStockAvailability,
  transformSearchspringResponseToAppFormat,
} from '../../../utils';
import { triggerBoardsModal } from '../../../utils/helpers';
import { mapProductToAnalyticsEcommerceItem } from '../../../utils/helpers/analytics';
import { ContentBlockModal } from '../ContentBlockModal';
import { ContentBlockModalDisplay } from '../ContentBlockModalDisplay';
import { CategoryType, OnClickCardInterface } from '../types';
import { getTaxonomies, getL2FromTaxonomy } from '../utilities';

async function postBlockData(
  url: string,
  {
    arg,
  }: {
    arg: {
      siteId: string;
      lang: string;
      customer: {
        id: string;
        email: string;
        projectId: number;
      };
      sessionId?: string;
      referrerUrl: string;
      id: string;
      mock: boolean;
      extraFields: string[];
    };
  },
) {
  return postFetcher(url, arg);
}
interface ContentBlocksModalContainerProps {
  handleProjectClick?: (
    projectid?: number,
    options?: { analyticsEventLocation?: string | undefined },
  ) => void;
  onProjectSelect?: (projectId: number) => void;
  selectedProject: StructuredProject;
  onCreateProjectClick?: (props: ProjectAnalyticsProps) => void;
}
export const ContentBlocksModalContainer: FC<
  ContentBlocksModalContainerProps
> = ({
  handleProjectClick,
  onProjectSelect,
  selectedProject,
  onCreateProjectClick,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });
  const activeContentBlockData: OnClickCardInterface = useSelector(
    activeContentBlockDataSelector,
  );
  const xRequestId = useSelector(xRequestIdSelector);
  const searchConfig = getSearchConfig();
  const customer = getUserAttributes();
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<
    ProductSearchMaterial[]
  >([]);
  const analyticsEventLocation = 'Content_Blocks';

  const { addToBag, isLoading: isBagLoading } = useAddToBag();

  const { currentProject } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  const { trigger, isMutating, data } = useSWRMutation(
    `${searchConfig.siteUrl}/contentblock/children?${activeContentBlockData?.id}`,
    postBlockData,
    { revalidate: false },
  );

  const [filteredResults, setFilteredResults] = useState<
    ProductSearchMaterial[]
  >([]);

  const [serverResults, setServerResults] = useState<ProductSearchMaterial[]>(
    [],
  );

  const categoryList: CategoryType[] = useMemo(
    () => getTaxonomies(data?.results),
    [data],
  );

  const handleClose = useCallback(() => {
    dispatch(setActiveContentBlock(null));
    dispatch(setContentBlockXRequestId(undefined));
    setActiveFilters([]);
    setFilteredResults([]);
    setSelectedMaterials([]);
    dispatch(setContentBlocksModalOpen(false));
  }, [dispatch]);

  useEffect(() => {
    if (activeContentBlockData) {
      trigger({
        siteId: searchConfig.siteId,
        // hardcoded for now
        lang: 'en',
        customer: {
          id: customer.customer_id,
          email: customer.email,
          projectId: parseInt(customer.current_project, 10) || 0,
        },
        sessionId: xRequestId,
        referrerUrl: `${window.location.hostname}${window.location.pathname}`,
        id: activeContentBlockData.id,
        mock: false,
        extraFields: extraFieldsList,
      });
    }
  }, [activeContentBlockData]);

  const onFilterResults = useCallback(
    (filterKey: string) => {
      // unselect all the selected materials
      setSelectedMaterials([]);
      const updatedFilters = activeFilters.includes(filterKey)
        ? activeFilters.filter((filter) => filter !== filterKey)
        : [...activeFilters, filterKey];

      setActiveFilters(updatedFilters);

      if (updatedFilters.length === 0) {
        setFilteredResults(serverResults || []);
        return;
      }

      const tempResults = serverResults.filter((result) => {
        if (result.taxonomyL2Display) {
          return updatedFilters.includes(result.taxonomyL2Display);
        }
        return false;
      });

      setFilteredResults(tempResults);
    },
    [serverResults, activeFilters],
  );

  const handleAssociatedFinishProductsModal = useCallback(() => {
    const itemsAssociatedFinishProducts: any[] = [];

    selectedMaterials.forEach((item) => {
      if ((item?.associatedFinishProducts || []).length > 0) {
        const finishItems = {
          associatedProducts: item.associatedFinishProducts,
          parentProduct: {
            id: item.id,
            manufacturerName: item.brand,
            name: item.title || item.name,
            productImageUrl: item.imageUrl,
          },
        };
        itemsAssociatedFinishProducts.push(finishItems);
      }
    });

    if (itemsAssociatedFinishProducts.length > 0) {
      window.dispatchEvent(
        new CustomEvent('open-associated-finishes-products-modal', {
          detail: [...itemsAssociatedFinishProducts],
        }),
      );
    }
  }, [filteredResults]);

  const handleAddToBag = () => {
    const eligibleProducts = selectedMaterials.filter(
      (item: ProductSearchMaterial) => !item?.isProduct && !item?.isDigital,
    );

    if (shouldTriggerProjectSelectionDialog()) {
      window.dispatchEvent(
        new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
          detail: {
            currentProjectId: selectedProject?.id,
            analyticsEventLocation,
            onCurrentProjectSave: (project: any) => {
              onProjectSelect?.(project?.externalId || project?.project_id);

              // No validation needed here - the project has been validated in the selection dialog
              addToBag({
                materials: eligibleProducts,
                projectId:
                  project?.externalId?.toString() ||
                  String(project?.project_id),
                project,
                triggerLocation: 'Catalog',
                isFinishMaterial: false,
                additionalAnalyticsProperties: {
                  listId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
                  listName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
                  itemListId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
                  itemListName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
                  eventLocation: 'Catalog',
                },
              });
            },
          },
        }),
      );
      return;
    }

    const addToBagPros = {
      materials: eligibleProducts,
      projectId: String(currentProject?.externalId),
      additionalAnalyticsProperties: {
        listId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
        listName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
        itemListId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
        itemListName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
        eventLocation: 'Catalog',
      },
    };

    // If the project is not found, open the create project modal
    if (!currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: {
            eventLocation: 'Catalog',
          },
        }),
      );

      return;
    }

    // Validate the project data
    if (currentProject && !isProjectDataComplete(currentProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: currentProject.externalId,
        successCallback: () => {
          addToBag(addToBagPros);
        },
      });

      return;
    }

    addToBag(addToBagPros);

    handleAssociatedFinishProductsModal();
  };

  const handleProductSelect = (product: ProductSearchMaterial) => {
    setSelectedMaterials((prevSelectedMaterials: ProductSearchMaterial[]) => {
      const isProductSelected = prevSelectedMaterials.some(
        (selectedMaterial) => selectedMaterial.entityId === product.entityId,
      );

      if (isProductSelected) {
        return prevSelectedMaterials.filter(
          (selectedMaterial) => selectedMaterial.entityId !== product.entityId,
        );
      }

      return [...prevSelectedMaterials, product];
    });
  };

  const handleAddToBoard = useCallback(async () => {
    const ids =
      selectedMaterials?.map((item: ProductSearchMaterial) => item.entityId) ??
      [];
    const analyticsProps = getEventsMaterialProps(selectedMaterials?.[0]);

    const addToWishlistEventItems =
      selectedMaterials?.map((selectedMaterial: ProductSearchMaterial) =>
        mapProductToAnalyticsEcommerceItem({
          product: selectedMaterial,
          itemListId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
          itemListName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
          project: currentProject,
        }),
      ) ?? [];

    const currencies = {
      US: 'USD',
      EU: 'EUR',
      JP: 'JPY',
    };

    const itemsData = selectedMaterials.map((item: any) => {
      const type: 'finish' | 'material' = item.productFinishType
        ?.toLowerCase()
        ?.includes('finish material')
        ? 'finish'
        : 'material';

      return {
        id: item.entityId,
        type,
      };
    });

    if (ids.length > 0) {
      triggerBoardsModal({
        itemsData,
        onSuccessAnalyticsEvents: [
          { ecommerce: null },
          {
            event: 'add_to_wishlist',
            list_id: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
            list_name: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
            item_list_id: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
            item_list_name: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
            method:
              filteredResults.length === selectedMaterials.length
                ? 'Add all'
                : 'Multi-select',
            type: 'Materials',
            name: null,
            action: null,
            experiment_name: null,
            experiment_version: null,
            event_location: 'Catalog',
            ecommerce: {
              currency: currencies[ENVIRONMENT_REGION],
              value: 0,
              items: addToWishlistEventItems,
            },
          },
        ],
      });
      const stringIds = ids.map((id: any) => String(id));
      const value = {};
      await sendAnalytics({
        id: stringIds,
        type: 'favorite',
        ...value,
        ...analyticsProps,
      });
    }
  }, [selectedMaterials, activeContentBlockData]);

  const handleSelectAll = useCallback(() => {
    setSelectedMaterials(
      filteredResults.map((product: any) => {
        if (product?.selectedColorFilterEntityId) {
          return {
            ...product,
            entityId: product.selectedColorFilterEntityId,
          };
        }

        return product;
      }),
    );
  }, [filteredResults]);

  const onHandleCancel = () => {
    setSelectedMaterials([]);
  };

  useEffect(() => {
    if (!isMutating && data) {
      const mappedResults = data.results.map(
        transformSearchspringResponseToAppFormat,
      );

      const updatedResults = mappedResults.map(
        (item: ProductSearchMaterial) => {
          const taxonomyL2 = getL2FromTaxonomy(item);

          return {
            ...item,
            taxonomyL2Display: taxonomyL2,
            analyticsData: {
              analyticsClass: 'recommend',
              impressions: mappedResults?.map((item: ProductSearchMaterial) =>
                String(item.entityId),
              ),
            },
            originRequestId: data?.headers,
          };
        },
      );
      setFilteredResults(updatedResults);
      setServerResults(updatedResults);

      checkStockAvailability(updatedResults)
        .then((response) => {
          setServerResults(response);
          setFilteredResults(response);
        })
        .catch((error) => {
          console.error('Error checking stock availability:', error);
        });
      dispatch(setContentBlockXRequestId(data?.headers));
      dataLayer.push({
        event: 'view_item_list',
        list_id: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
        list_name: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
        event_location: 'Catalog',
        item_list_id: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
        item_list_name: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
        ecommerce: {
          items: updatedResults?.map(
            (selectedMaterial: ProductSearchMaterial) =>
              mapProductToAnalyticsEcommerceItem({
                product: selectedMaterial,
                itemListId: `contentblocks_catalog_${activeContentBlockData.id}_${activeContentBlockData.indexLocation}`,
                itemListName: `contentblocks catalog ${activeContentBlockData.id} ${activeContentBlockData.indexLocation}`,
                project: currentProject,
              }) ?? [],
          ),
        },
      });
    }
  }, [data, isMutating]);

  return (
    <ContentBlockModal
      open={!isMutating}
      visible
      onClose={handleClose}
      isMobile={isMobile}
    >
      <ContentBlockModalDisplay
        id={activeContentBlockData.id}
        indexLocation={activeContentBlockData.indexLocation}
        isLoading={isMutating}
        cardAttributes={data?.cardAttributes}
        results={filteredResults}
        activeFilters={activeFilters}
        categories={categoryList}
        onCategoryClick={onFilterResults}
        handleAddToBag={handleAddToBag}
        onSelectAll={handleSelectAll}
        handleAddToBoard={handleAddToBoard}
        onHandleCancel={onHandleCancel}
        handleProductSelect={handleProductSelect}
        selectedProducts={selectedMaterials}
        isMobile={isMobile}
        isBagLoading={isBagLoading}
        handleProjectClick={handleProjectClick}
        onProjectSelect={onProjectSelect}
        selectedProject={selectedProject}
        onCreateProjectClick={onCreateProjectClick}
      />
    </ContentBlockModal>
  );
};
